import { cache } from 'react';

import { createServerApi } from '@/lib/server-api';
import { getSeoResolveSSR } from '@/features/seo-listing/api';
import { parseListingGetUrl } from '@/features/seo-listing/utils/parse-listing-get-url.util';
import { buildFandomPermalinkHref } from '@/features/seo-listing/utils/seo-listing';

type FandomCustomRow = {
  id?: number | string;
  title?: string | null;
  sec_title?: string | null;
  slug?: string | null;
};

export type ResolvedFandomForCharactersHub = {
  fandomId: string;
  fandomTitle: string;
};

/**
 * Characters hub needs a numeric fandom id. Prefer SEO resolve (enabled pages),
 * otherwise look up the fandom by exact slug via fandoms-custom so hubs stay
 * reachable when permanentUrl/SEO page is missing (e.g. Genshin on stage).
 * React.cache dedupes generateMetadata + page within the same request.
 */
export const resolveFandomForCharactersHub = cache(
  async (fandomSlug: string): Promise<ResolvedFandomForCharactersHub | null> => {
    const resolveResult = await getSeoResolveSSR(buildFandomPermalinkHref(fandomSlug));
    if (resolveResult.status === 'ok') {
      const fandomId = parseListingGetUrl(resolveResult.data.listingGetUrl).fandoms[0];
      if (fandomId) {
        return {
          fandomId,
          fandomTitle: resolveResult.data.pageName,
        };
      }
    }

    try {
      const serverApi = await createServerApi();
      const params = new URLSearchParams({
        search: fandomSlug,
        limit: '20',
        type: 'fanfic',
      });
      const response = await serverApi.get(`/api/fandoms-custom?${params.toString()}`);
      const rows = (response.data?.fandoms ?? []) as FandomCustomRow[];
      const match = rows.find((row) => row.slug?.trim() === fandomSlug);
      if (!match?.id) return null;
      return {
        fandomId: String(match.id),
        fandomTitle: (match.title || match.sec_title || fandomSlug).trim(),
      };
    } catch {
      return null;
    }
  },
);
